import { useMemo } from 'react';

export interface ResponsiveBreakpoints {
  isSmall: boolean;
  isMedium: boolean;
  isLarge: boolean;
  isXLarge: boolean;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  isLargeDesktop: boolean;
  isShortScreen: boolean;
  isMediumScreen: boolean;
  isTallScreen: boolean;
  isMobilePortrait: boolean;
  isMobileLandscape: boolean;
  isCompactHeight: boolean;
}

export interface ResponsiveScaling {
  scaleFactor: number;
  fontSize: number;
  spacing: number;
  iconSize: number;
  buttonHeight: number;
  cardPadding: number;
}

const WEB_WIDTH =
  typeof window !== 'undefined' ? window.innerWidth : 1280;
const WEB_HEIGHT =
  typeof window !== 'undefined' ? window.innerHeight : 800;

/** Web login/auth screens use CSS media queries — no resize listeners. */
export const useResponsiveWeb = () => {
  return useMemo(() => {
    const breakpoints: ResponsiveBreakpoints = {
      isSmall: WEB_WIDTH < 768,
      isMedium: WEB_WIDTH >= 768 && WEB_WIDTH < 1024,
      isLarge: WEB_WIDTH >= 1024 && WEB_WIDTH < 1440,
      isXLarge: WEB_WIDTH >= 1440,
      isMobile: WEB_WIDTH < 768,
      isTablet: WEB_WIDTH >= 768 && WEB_WIDTH < 1024,
      isDesktop: WEB_WIDTH >= 1024,
      isLargeDesktop: WEB_WIDTH >= 1440,
      isShortScreen: WEB_HEIGHT < 600,
      isMediumScreen: WEB_HEIGHT >= 600 && WEB_HEIGHT < 900,
      isTallScreen: WEB_HEIGHT >= 900,
      isMobilePortrait: WEB_WIDTH < 768 && WEB_HEIGHT > WEB_WIDTH,
      isMobileLandscape: WEB_WIDTH < 768 && WEB_WIDTH > WEB_HEIGHT,
      isCompactHeight: WEB_HEIGHT < 700,
    };

    const scaling: ResponsiveScaling = {
      scaleFactor: 1,
      fontSize: 1,
      spacing: 1,
      iconSize: 1,
      buttonHeight: 1,
      cardPadding: 1,
    };

    const getResponsiveValue = (baseValue: number, type: keyof ResponsiveScaling = 'scaleFactor') =>
      Math.max(baseValue * scaling[type], baseValue * 0.5);

    return {
      width: WEB_WIDTH,
      height: WEB_HEIGHT,
      breakpoints,
      scaling,
      isWeb: true,
      getResponsiveValue,
      getResponsivePadding: (basePadding: number) => basePadding,
      getResponsiveStyles: () => ({
        spacing: { xs: 8, sm: 12, md: 16, lg: 24, xl: 32, xxl: 48 },
        container: {
          maxWidth: '70%',
          minWidth: 500,
          padding: { horizontal: '8%', vertical: '4%' },
        },
        typography: { xs: 12, sm: 14, base: 16, lg: 18, xl: 20, xxl: 24 },
        components: {
          cardRadius: 16,
          buttonHeight: 48,
          inputHeight: 44,
          iconSize: 24,
        },
      }),
      getResponsiveFontSize: (baseFontSize: number) => Math.max(baseFontSize, 12),
      getResponsiveIconSize: (baseIconSize: number) => Math.max(baseIconSize, 16),
      getResponsiveButtonHeight: (baseHeight: number) => Math.max(baseHeight, 36),
      getResponsiveCardPadding: (basePadding: number) => Math.max(basePadding, 8),
    };
  }, []);
};

export default useResponsiveWeb;
